import { isValidPin } from "../lib/PinValidator.js";

export const SAVED_CREDENTIALS_KEY = "credentials:encrypted:v1";
const ITERATIONS = 600000;
const encoder = new TextEncoder();

const validCredentials = (value) => value && /^[0-9]{7}$/.test(value.customerNumber) &&
    typeof value.user === "string" && value.user.trim().length > 0 &&
    typeof value.password === "string" && value.password.length > 0;

async function deriveKey(pin, salt, usage) {
    const material = encoder.encode(pin);
    try {
        const baseKey = await crypto.subtle.importKey("raw", material, "PBKDF2", false, ["deriveKey"]);
        return await crypto.subtle.deriveKey(
            { name: "PBKDF2", hash: "SHA-256", salt, iterations: ITERATIONS }, baseKey,
            { name: "AES-GCM", length: 256 }, false, [usage]
        );
    } finally {
        material.fill(0);
    }
}

/** Stable endpoint scope: task identifiers never belong in the remembered-account key. */
export async function createCredentialsContext({ apiUrl, apiUrlTemplate, taskId, config = {}, customerId }) {
    const endpoint = new URL(apiUrlTemplate || apiUrl);
    const normalize = (value) => value === String(taskId) ? "{task}" : value.replaceAll("${taskId}", "{task}");
    const path = endpoint.pathname.split("/").map((part) => normalize(decodeURIComponent(part))).join("/");
    const query = [...endpoint.searchParams].map(([name, value]) => [name, normalize(value)]).sort();
    const scope = JSON.stringify([endpoint.origin, path, query, config.env ?? null,
        config.customerId ?? config.customerNumber ?? customerId ?? null]);
    const digest = await crypto.subtle.digest("SHA-256", encoder.encode(scope));
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export class CredentialsService {
    constructor(storage) {
        this.storage = storage;
        this.context = null;
        this.clearMemory();
    }

    initialize(context) {
        this.clearMemory();
        this.context = context;
    }

    clearMemory() {
        this.credentials = null;
        this.generation = (this.generation ?? 0) + 1;
    }

    getCredentials() {
        return this.credentials ? { ...this.credentials } : null;
    }

    async hasSavedCredentials() {
        const record = await this.storage.getItem(SAVED_CREDENTIALS_KEY);
        return record?.context === this.context;
    }

    async useCredentials(credentials, pin = null) {
        this.clearMemory();
        const generation = this.generation;
        if (!validCredentials(credentials)) throw new Error("invalidCredentials");
        const clean = { customerNumber: credentials.customerNumber, user: credentials.user, password: credentials.password };
        let record = null;
        if (pin !== null) {
            if (!isValidPin(pin)) throw new Error("weakPin");
            if (!this.context) throw new Error("storageUnavailable");
            const salt = crypto.getRandomValues(new Uint8Array(16));
            const iv = crypto.getRandomValues(new Uint8Array(12));
            const key = await deriveKey(pin, salt, "encrypt");
            const plaintext = encoder.encode(JSON.stringify(clean));
            try {
                const ciphertext = await crypto.subtle.encrypt(
                    { name: "AES-GCM", iv, additionalData: encoder.encode(this.context) }, key, plaintext
                );
                record = { version: 1, context: this.context, iterations: ITERATIONS,
                    salt: [...salt], iv: [...iv], ciphertext: [...new Uint8Array(ciphertext)] };
            } finally {
                plaintext.fill(0);
            }
        }
        if (generation !== this.generation) throw new DOMException("", "AbortError");
        if (record) {
            try {
                await this.storage.setItem(SAVED_CREDENTIALS_KEY, record);
            } catch {
                throw new Error("storageUnavailable");
            }
            if (generation !== this.generation) throw new DOMException("", "AbortError");
        }
        this.credentials = clean;
    }

    async unlockWithPin(pin) {
        this.clearMemory();
        const generation = this.generation;
        if (!/^[0-9]{5}$/.test(pin)) throw new Error("invalidPin");
        try {
            const record = await this.storage.getItem(SAVED_CREDENTIALS_KEY);
            const bytes = (value, length) => Array.isArray(value) && value.length === length &&
                value.every((byte) => Number.isInteger(byte) && byte >= 0 && byte <= 255);
            if (record?.version !== 1 || record.context !== this.context || record.iterations !== ITERATIONS ||
                !bytes(record.salt, 16) || !bytes(record.iv, 12) || !Array.isArray(record.ciphertext) ||
                record.ciphertext.length < 16 || record.ciphertext.length > 65536 ||
                !bytes(record.ciphertext, record.ciphertext.length)) throw new Error();
            const key = await deriveKey(pin, new Uint8Array(record.salt), "decrypt");
            const plaintext = new Uint8Array(await crypto.subtle.decrypt(
                { name: "AES-GCM", iv: new Uint8Array(record.iv), additionalData: encoder.encode(this.context) },
                key, new Uint8Array(record.ciphertext)
            ));
            try {
                const credentials = JSON.parse(new TextDecoder().decode(plaintext));
                if (!validCredentials(credentials)) throw new Error();
                if (generation !== this.generation) throw new DOMException("", "AbortError");
                this.credentials = credentials;
            } finally {
                plaintext.fill(0);
            }
        } catch (error) {
            if (error.name === "AbortError") throw error;
            throw new Error("invalidPin");
        }
    }

}
